import React from "react";
import { styled } from "@mui/material/styles";
import { Chip } from "@mui/material";
import { css } from "@emotion/react";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";

type ParticipantTypeChipProps = {
  variant: "outlined" | "filled";
  color: "default" | "blue";
  deleteIcon?: JSX.Element;
  onDelete?: React.MouseEventHandler;
};

type Props = {
  displayName: string;
  isLocked: boolean;
  isClickable: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
};

const ParticipantTypeTagStatic: React.FC<Props> = ({
  displayName,
  isLocked,
  isClickable,
  onClick,
  disabled = false,
}) => {
  const chipProps = React.useMemo<ParticipantTypeChipProps>(() => {
    if (disabled) {
      return { variant: "outlined", color: "default" };
    } else if (isLocked) {
      return { variant: "filled", color: "blue", onDelete: onClick, deleteIcon: <FontIcon name="i-toggle-locked" /> };
    }

    return { variant: "filled", color: "default" };
  }, [isLocked, disabled, onClick]);

  return (
    <StyledChip
      label={displayName}
      color={chipProps.color}
      variant={chipProps.variant}
      clickable={isClickable}
      data-testid={"ParticipantTypeTag"}
      $isLocked={isLocked}
      $disabled={disabled}
      deleteIcon={chipProps.deleteIcon}
      onClick={onClick}
      onDelete={chipProps.onDelete}
    />
  );
};

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => !["$disabled", "$isLocked"].includes(prop.toString()),
})<{
  $isLocked: boolean;
  $disabled?: boolean;
}>(
  ({ theme, $isLocked, $disabled }) => css`
    ${$isLocked
      ? `
        .MuiChip-deleteIcon {
          color: inherit;
          font-weight: 600;
        }
      `
      : `
        background-color: ${theme.palette.background.default};
        color: ${theme.palette.text.secondary};
      `}

    ${$disabled &&
    `
        background-color: ${theme.palette.background.paper};
        border: 1px solid ${theme.palette.grey[500]};
        color: ${theme.palette.text.disabled};
      `}
  `
);

export default ParticipantTypeTagStatic;
